import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/** Native modal keeps focus inside and makes the background inert. */
export function Modal({
  title,
  children,
  onClose,
  busy = false,
  closing = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  busy?: boolean;
  closing?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const dialog = ref.current;
    dialog?.showModal();
    return () => {
      dialog?.close();
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);
  return createPortal(
    <dialog
      ref={ref}
      data-closing={closing || undefined}
      aria-label={title}
      aria-busy={busy || undefined}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy && !closing) onClose();
      }}
      className="rimati-tool-modal m-auto w-[calc(100%_-_2rem)] max-w-3xl max-h-[90dvh] overflow-hidden rounded-card border border-line bg-surface p-0 text-ink shadow-xl backdrop:bg-ink/40"
    >
      <div className="flex max-h-[90dvh] flex-col">
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-line px-5 py-4">
          <h2 className="min-w-0 break-words text-base font-semibold leading-relaxed">{title}</h2>
          <button
            type="button"
            disabled={busy || closing}
            autoFocus
            aria-label="Fechar janela"
            onClick={onClose}
            className="flex size-11 shrink-0 items-center justify-center rounded-control text-ink-muted hover:bg-surface-sunken"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>
        <div className="min-h-0 overflow-y-auto overscroll-contain p-5 sm:p-6">{children}</div>
      </div>
    </dialog>,
    document.body,
  );
}
